import { useState, useEffect } from "react";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Zap, Sun, BatteryCharging } from "lucide-react";
import axios from "axios";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

const CATEGORY_META = {
  Pannelli: { icon: Sun, color: "emerald", desc: "I migliori pannelli fotovoltaici selezionati per efficienza e durata" },
  Inverter: { icon: Zap, color: "blue", desc: "Inverter ad alta efficienza per massimizzare la produzione" },
  Batterie: { icon: BatteryCharging, color: "amber", desc: "Sistemi di accumulo per l'autoconsumo totale" },
};

const colorMap = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600", iconBg: "bg-emerald-100" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-600", iconBg: "bg-blue-100" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-600", iconBg: "bg-amber-100" },
};

export default function ProductsPage() {
  const [brands, setBrands] = useState({});
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "I Nostri Partner | Fotovoltaico Chiavi in Mano",
    description: "Scopri i brand partner selezionati: pannelli, inverter e batterie delle migliori marche per il tuo impianto fotovoltaico.",
  });

  useEffect(() => {
    axios.get(`${API}/brands`)
      .then((r) => setBrands(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="products-page">
      {/* Navbar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="products-home-link">
            <img src={LOGO_URL} alt="Logo" className="h-10 w-auto" />
          </a>
          <a href="/" className="text-sm text-slate-500 hover:text-emerald-600 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 sm:py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">I Nostri Partner</span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="products-title">
            Marchi di Qualita
          </h1>
          <p className="mt-4 text-slate-500 text-base sm:text-lg max-w-2xl mx-auto">
            Lavoriamo esclusivamente con i migliori brand del settore fotovoltaico per garantirti affidabilita, prestazioni e durata nel tempo.
          </p>
        </div>
      </section>

      {/* Brand Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {loading ? (
          <div className="py-20 text-center text-slate-400">Caricamento...</div>
        ) : (
          Object.entries(CATEGORY_META).map(([category, meta], index) => {
            const items = brands[category] || [];
            const c = colorMap[meta.color];
            const Icon = meta.icon;
            return (
              <section key={category} className={index > 0 ? "mt-16 pt-10 border-t border-slate-100" : ""} data-testid={`brand-section-${category.toLowerCase()}`}>
                <div className="flex items-center gap-4 mb-8">
                  <div className={`w-12 h-12 rounded-xl ${c.iconBg} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${c.text}`} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {category}
                    </h2>
                    <p className="text-sm text-slate-500">{meta.desc}</p>
                  </div>
                </div>

                {items.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-sm bg-white rounded-2xl border border-slate-100">
                    Nessun brand in questa categoria
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                    {items.map((brand) => (
                      <div key={brand.id}
                        className={`bg-white rounded-2xl border ${c.border} p-6 flex flex-col items-center justify-center text-center hover:shadow-lg transition-all group`}
                        data-testid={`brand-card-${brand.id}`}>
                        <div className="w-full h-20 flex items-center justify-center mb-4">
                          <img
                            src={brand.logo_url}
                            alt={brand.name}
                            className="max-h-16 max-w-[120px] object-contain group-hover:scale-105 transition-transform"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {brand.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}
